import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import AccordionStep from './components/AccordionStep';
import OrderProductConfirmation from './components/OrderProductConfirmation';
import ComplaintIdentification from './components/ComplaintIdentification';
import Verification from './components/Verification';
import Resolution from './components/Resolution';
import ResolutionConfirmation from './components/ResolutionConfirmation';
import CannotVerifyResponse from './components/CannotVerifyResponse';
import { COPILOT_STEPS, STEP_STATUS } from './constants';
import copilotModel from '../../../api/lifeline/copilot';
import tagsModel from '../../../api/lifeline/tags';
import { useQueryCall, useMutationCall } from '../../../api/react-client';
import { lifelineEndPoints } from '../../../api/end-points';
import {
  useCopilotPolling,
  COPILOT_POLL_STATUS,
} from './hooks/useCopilotPolling';
import { MetricsForJumboEvent } from '../../../analytics/constants';
import {
  CopilotButtonClickedEvent,
  CopilotFlowDropOffEvent,
  Step1OpenedEvent,
  Step1ProductSelectionChangedEvent,
  Step1CtaClickedEvent,
  Step1CtaResponseEvent,
  Step2OpenedEvent,
  Step2ComplaintTypeChangedEvent,
  Step2CtaClickedEvent,
  Step2CtaResponseEvent,
  Step3OpenedEvent,
  Step3GrievanceLevelSelectedEvent,
  Step3CtaClickedEvent,
  Step3CtaResponseEvent,
  Step3CannotVerifyResponseShownEvent,
  Step4OpenedEvent,
  Step4CtaClickedEvent,
  Step4CtaResponseEvent,
  Step4TagsAddedOnCloseEvent,
} from '../../../analytics/copilot/events';
import {
  COPILOT_ADD_MANUAL_TAG_URL,
  toDisplayProducts,
  toTagOptions,
  toResolutionItems,
  toSourceOptions,
  toDeniedItems,
} from './utils';

/**
 * Agent Copilot side panel - Phase 0.
 *
 * Owns the cross-step state: which step is open, each step's status
 * (pending/active/completed), and the data one step's confirmation hands
 * to the next (confirmed products -> complaints -> verification result).
 * Each step component stays responsible for its own in-progress form
 * state and only reports back the final, confirmed value.
 */
const CopilotView = ({
  orderId = null,
  cartId = null,
  customerId = null,
  onClose = () => {},
}) => {
  // Owned by the frontend for this raise-issue flow - must be sent again,
  // unchanged, on every later copilot call, so it's generated once per
  // Copilot session rather than per request.
  const [sessionId] = useState(() => uuidv4());
  const [openStep, setOpenStep] = useState(1);
  // confirm_items' and confirm_complaint's own responses now carry the
  // next step's guidance synchronously (resolved from an admin-configured
  // policy lookup, not a polled Agent Platform session) - there's no more
  // separate identify_complaint/verify_complaint step to poll, so these
  // just hold each call's result directly, overwritten on every redo
  // (e.g. going back and reconfirming step 1 with a different selection).
  const [confirmItemsResult, setConfirmItemsResult] = useState(null);
  const [confirmComplaintResult, setConfirmComplaintResult] = useState(null);
  // Bumped each time the corresponding confirm step succeeds (including a
  // redo after going back and changing the selection), so a redo always
  // starts a genuinely fresh poll instead of continuing to serve the
  // previous cycle's cached, now-stale response.
  const [evaluateAttempt, setEvaluateAttempt] = useState(0);
  const [cannotVerifyAttempt, setCannotVerifyAttempt] = useState(0);
  // Set once `submit_verification` succeeds with `can_verify: false` - its
  // presence is what appends the CannotVerifyResponse card below the step
  // list and gates the `cannot_verify_response` poll.
  const [cannotVerifyTriggered, setCannotVerifyTriggered] = useState(false);
  const [stepStatus, setStepStatus] = useState({
    1: STEP_STATUS.ACTIVE,
    2: STEP_STATUS.PENDING,
    3: STEP_STATUS.PENDING,
    4: STEP_STATUS.PENDING,
  });
  const [stepSummaries, setStepSummaries] = useState({});
  // Set once "/resolve" succeeds - its presence is what swaps step 4's
  // content from the Resolution form to the post-resolve confirmation
  // screen.
  const [resolutionResult, setResolutionResult] = useState(null);

  // Step 1's product picks and step 2's complaint-type pick live here
  // (rather than as local state inside OrderProductConfirmation/
  // ComplaintIdentification) because AccordionStep unmounts a step's
  // content while it's collapsed - local state there would reset every
  // time the agent closed a step (e.g. to look at a later one) and
  // reopened it. Keyed by item id -> selected quantity (0 = not selected).
  const [productCounts, setProductCounts] = useState({});
  const [selectedComplaintParentSlug, setSelectedComplaintParentSlug] =
    useState(null);
  const [selectedComplaintSubSlug, setSelectedComplaintSubSlug] =
    useState(null);
  const [checkedVerificationIds, setCheckedVerificationIds] = useState(
    () => new Set(),
  );
  // Keyed by `${itemId}:${rowId}` -> grievance_level_slug, same lifting
  // reason as above.
  const [grievanceByRowKey, setGrievanceByRowKey] = useState({});
  // Keyed by item id -> that item's rows, each `{rowId, quantity}` -
  // starts as a single row holding the item's full quantity; decrementing
  // a row peels 1 unit off into a brand new row (own blank grievance),
  // up to one row per unit, so e.g. a qty-3 item can end up as three
  // qty-1 rows each with its own grievance. Seeded lazily once
  // `step3Products` loads (see the effect below) - absent until then.
  const [rowsByItemId, setRowsByItemId] = useState({});

  // agent_id isn't repeated below - jumboClient already attaches it to every event.
  const jumboIdentity = {
    [MetricsForJumboEvent.order_id]: orderId,
    [MetricsForJumboEvent.cart_id]: cartId,
    [MetricsForJumboEvent.customer_id]: customerId,
  };

  // Fires once per session, when the panel actually mounts.
  useEffect(() => {
    CopilotButtonClickedEvent(jumboIdentity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop-off only counts if the flow wasn't already resolved.
  const handleClose = () => {
    if (!resolutionResult) {
      CopilotFlowDropOffEvent({
        ...jumboIdentity,
        [MetricsForJumboEvent.feature_meta]: {
          open_step: openStep,
          step_status: JSON.stringify(stepStatus),
        },
      });
    }
    onClose();
  };

  const { data, isLoading, isError } = useQueryCall(
    { enabled: Boolean(orderId && cartId) },
    null,
    ['copilot-menu', orderId, cartId],
    () => copilotModel.getMenu({ order_id: orderId, cart_id: cartId }),
  );

  const items = (data?.multi_select_range?.source || []).filter(
    (item) => item?.id != null,
  );

  // Seeds each newly-seen item's default count exactly once (from the menu
  // API's own `is_selected`/`quantity_selected` flags) without touching any
  // item the agent has already toggled - `items` comes from an async fetch
  // so this can't just be a `useState` lazy initializer.
  useEffect(() => {
    if (items.length === 0) return;
    setProductCounts((prev) => {
      let changed = false;
      const next = { ...prev };
      items.forEach((item) => {
        if (!(item.id in next)) {
          next[item.id] = item.is_selected
            ? item.quantity_selected > 0
              ? item.quantity_selected
              : 1
            : 0;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Fires once the menu loads (not at mount - the item list isn't ready yet).
  const hasFiredStep1OpenedRef = useRef(false);
  useEffect(() => {
    if (hasFiredStep1OpenedRef.current || items.length === 0) return;
    hasFiredStep1OpenedRef.current = true;
    Step1OpenedEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        already_selected_items: JSON.stringify(
          items
            .filter((item) => item.is_selected)
            .map((item) => ({
              item_id: item.id,
              product_id: item.product_id,
              name: item.text,
              quantity: item.quantity_selected,
            })),
        ),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Same endpoint used by "View Complaint Images" elsewhere in the
  // dashboard (OrderIssuesImagesV1) - reused here for steps 2/3's evidence
  // strip. Only fetched once one of those steps is actually open, not
  // pre-emptively.
  const getComplaintImagesEndPoint = `${lifelineEndPoints.GET_CHAT_MEDIA}?order_id=${orderId}&customer_id=${customerId}&offset=0&type=complaint`;
  const { data: complaintMediaData } = useQueryCall(
    {
      enabled: Boolean(
        orderId && customerId && (openStep === 2 || openStep === 3),
      ),
    },
    getComplaintImagesEndPoint,
    ['copilot-complaint-images', orderId, customerId],
  );

  const complaintImages = (complaintMediaData?.media_data || []).map(
    (media, index) => ({
      id: media?.media_id ?? index,
      url: media?.media_url,
      type: (media?.media_type || 'image').toLowerCase(),
      alt: `Uploaded evidence ${index + 1}`,
    }),
  );

  // confirm_items' own response now carries this synchronously - see
  // ConfirmItemsService.ConfirmItems (CopilotPolicy-resolved, not a polled
  // Agent Platform session).
  const step2Products = toDisplayProducts(confirmItemsResult?.selectedItems);

  // Treated as a binary "do we have it yet" rather than a real
  // Pending/Completed/Failed tri-state - confirm_items' issue lookup is
  // best-effort or not resolved into `options` at all; a lookup failure
  // there just means fewer options, not a FAILED response to show here.
  const complaintIdentificationStatus = confirmItemsResult
    ? COPILOT_POLL_STATUS.COMPLETED
    : null;

  // Each parent complaint type may carry its own `sub_options` - the
  // dropdown the agent actually confirms against is whichever is more
  // specific (see ComplaintIdentification.js). Sub-options' label field is
  // `issue` (CopilotPolicy's own field name), not `display_text` like the
  // parent complaint-type options - a different shape than the Agent
  // Platform's old sub_options carried.
  const complaintTypeOptions = (confirmItemsResult?.options || []).map(
    (option) => {
      const seenSlugs = new Set();
      const subOptions = [];
      for (const sub of option.sub_options || []) {
        if (seenSlugs.has(sub.slug)) continue;
        seenSlugs.add(sub.slug);
        subOptions.push({ label: sub.issue, value: sub.slug });
      }
      return { label: option.display_text, value: option.slug, subOptions };
    },
  );

  // The confirmed slug may be a parent's or a sub-type's - search both
  // levels for its display label rather than falling back to the raw slug.
  const findComplaintTypeLabel = (slug) => {
    for (const option of complaintTypeOptions) {
      if (option.value === slug) return option.label;
      const subMatch = option.subOptions.find((sub) => sub.value === slug);
      if (subMatch) return subMatch.label;
    }
    return slug;
  };

  // confirm_complaint's own response now carries this synchronously - see
  // ConfirmComplaintService.ConfirmComplaint (CopilotPolicy-resolved, not a
  // polled Agent Platform session). Unlike confirm_items' issue lookup,
  // this one has real Completed/Failed semantics (Failed when the
  // confirmed slug matches no configured issue), so the status comes
  // straight off the response rather than being synthesized.
  const step3Products = toDisplayProducts(
    confirmComplaintResult?.data?.selected_items,
  );

  const verificationSteps = (confirmComplaintResult?.data?.options || []).map(
    (option) => ({ id: option.slug, label: option.display_text }),
  );

  // Seeds each newly-seen item's rows to a single row holding its full
  // quantity, exactly once - `step3Products` arrives asynchronously (the
  // `confirm_complaint` response), so this can't just be a `useState`
  // lazy initializer. Doesn't touch an item the agent has already split,
  // same pattern as the other per-step seeding effects in this file.
  useEffect(() => {
    if (step3Products.length === 0) return;
    setRowsByItemId((prev) => {
      let changed = false;
      const next = { ...prev };
      step3Products.forEach((product) => {
        if (!(product.id in next)) {
          next[product.id] = [{ rowId: 'r0', quantity: product.count }];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step3Products]);

  // Expands each item into one row per its `rowsByItemId` entry - a fresh
  // item (not seeded yet) falls back to a single implicit full-quantity
  // row so step 4 opening right after step 3 completes doesn't momentarily
  // see zero rows.
  const step3Rows = step3Products.flatMap((product) => {
    const rows = rowsByItemId[product.id] || [
      { rowId: 'r0', quantity: product.count },
    ];
    return rows.map((row) => {
      const rowKey = `${product.id}:${row.rowId}`;
      return {
        ...product,
        rowKey,
        rowId: row.rowId,
        count: row.quantity,
        maxCount: product.count,
        canSplit: row.quantity > 1,
        canMerge: rows.length > 1,
        selectedGrievance: grievanceByRowKey[rowKey] || null,
      };
    });
  });

  // Peels 1 unit off `rowId` into a brand new row (its own blank
  // grievance) - only valid while that row still holds more than 1 unit,
  // so an item's rows can never exceed its total quantity.
  const handleSplitRow = (itemId, rowId) => {
    setRowsByItemId((prev) => {
      const rows = prev[itemId] || [];
      const rowIndex = rows.findIndex((row) => row.rowId === rowId);
      if (rowIndex === -1 || rows[rowIndex].quantity <= 1) return prev;
      const nextRows = rows.map((row, index) =>
        index === rowIndex ? { ...row, quantity: row.quantity - 1 } : row,
      );
      nextRows.push({ rowId: uuidv4(), quantity: 1 });
      return { ...prev, [itemId]: nextRows };
    });
  };

  // Reverses the most recent split into `rowId` - pulls 1 unit back from
  // whichever other row was split off last (scanning from the end),
  // dropping that row entirely once it's emptied out.
  const handleMergeRow = (itemId, rowId) => {
    setRowsByItemId((prev) => {
      const rows = prev[itemId] || [];
      const rowIndex = rows.findIndex((row) => row.rowId === rowId);
      const sourceIndex = [...rows]
        .map((row, index) => ({ row, index }))
        .reverse()
        .find(
          ({ row, index }) => index !== rowIndex && row.quantity > 0,
        )?.index;
      if (rowIndex === -1 || sourceIndex === undefined) return prev;
      const nextRows = rows
        .map((row, index) => {
          if (index === rowIndex) return { ...row, quantity: row.quantity + 1 };
          if (index === sourceIndex)
            return { ...row, quantity: row.quantity - 1 };
          return row;
        })
        .filter((row) => row.quantity > 0);
      return { ...prev, [itemId]: nextRows };
    });
  };

  // Fires every time the agent opens (or re-opens) step 2/3.
  useEffect(() => {
    if (openStep === 2) {
      Step2OpenedEvent({
        ...jumboIdentity,
        [MetricsForJumboEvent.feature_meta]: {
          product_count: step2Products.length,
          complaint_type_option_count: complaintTypeOptions.length,
        },
      });
    } else if (openStep === 3) {
      Step3OpenedEvent({
        ...jumboIdentity,
        [MetricsForJumboEvent.feature_meta]: {
          verification_steps: JSON.stringify(verificationSteps),
          selected_items: JSON.stringify(
            step3Products.map((product) => ({
              item_id: product.id,
              product_id: product.productId,
              name: product.name,
              quantity: product.count,
            })),
          ),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openStep]);

  // Step 3's "active" transition only happens in `onSuccess`, once the
  // backend confirms the slug actually matched a configured issue - a
  // Failed status (unknown slug) must not advance past step 2.
  const { mutate: confirmComplaint } = useMutationCall(
    {
      onSuccess: (res, slug) => {
        if (res?.status === COPILOT_POLL_STATUS.FAILED) {
          toast.error(res?.error ?? 'Could not confirm complaint');
          Step2CtaResponseEvent({
            ...jumboIdentity,
            [MetricsForJumboEvent.feature_meta]: {
              success: false,
              slug,
              error: res?.error,
            },
          });
          return;
        }
        Step2CtaResponseEvent({
          ...jumboIdentity,
          [MetricsForJumboEvent.feature_meta]: { success: true, slug },
        });
        setConfirmComplaintResult({ status: res?.status, data: res?.data });
        // A redo (reopening step 2 with a different complaint type) resolves
        // a new verification checklist - stale checked ids from the
        // previous checklist would otherwise still count toward
        // `allStepsChecked` even though none of the new checklist's items
        // are actually ticked.
        setCheckedVerificationIds(new Set());
        setGrievanceByRowKey({});
        setRowsByItemId({});
        completeStep(2, findComplaintTypeLabel(slug));
      },
      onError: (error, slug) => {
        toast.error(
          error?.message ?? 'Could not confirm complaint, please try again',
        );
        Step2CtaResponseEvent({
          ...jumboIdentity,
          [MetricsForJumboEvent.feature_meta]: {
            success: false,
            slug,
            error: error?.message,
          },
        });
      },
    },
    null,
    ['copilot-confirm-complaint', orderId],
    { doNotShowSuccessToast: true },
    (slug) =>
      copilotModel.confirmComplaint({ order_id: orderId, slug }, sessionId),
  );

  // Step 4 only opens once `/submit_verification` actually succeeds AND the
  // agent hit "Verify" (not "Cannot Verify") - `variables.body.can_verify`
  // is the mutation's own request, available as the `variables` arg of
  // `onSuccess`. `mutate()` only takes one variable, so `sessionId` (a
  // query param, not a body field) is bundled alongside `body` rather than
  // passed as a second argument.
  //
  // "Cannot Verify" takes a different path entirely: there's no step 4 to
  // open (no resolution to walk through once verification has failed), so
  // step 3 is marked completed directly here rather than via `completeStep`
  // - that helper always advances and opens the next step, which would
  // wrongly make step 4 reachable. `cannotVerifyTriggered` instead appends
  // the CannotVerifyResponse card below the (still-visible) step list (see
  // render below), with step 4 left locked.
  const { mutate: submitVerification } = useMutationCall(
    {
      onSuccess: (res, variables) => {
        if (res?.success === false) {
          toast.error(res?.message ?? 'Could not verify');
          Step3CtaResponseEvent({
            ...jumboIdentity,
            [MetricsForJumboEvent.feature_meta]: {
              success: false,
              can_verify: variables?.body?.can_verify,
              error: res?.message,
            },
          });
          return;
        }
        Step3CtaResponseEvent({
          ...jumboIdentity,
          [MetricsForJumboEvent.feature_meta]: {
            success: true,
            can_verify: variables?.body?.can_verify,
          },
        });
        if (variables?.body?.can_verify) {
          setEvaluateAttempt((attempt) => attempt + 1);
          // A prior "Cannot Verify" (on this same step-3 pass) may have
          // left its recommended-response card queued/shown - this
          // "Verify" succeeding supersedes that outcome entirely, so it
          // must not keep surfacing once step 4 opens.
          setCannotVerifyTriggered(false);
          completeStep(3, 'Verified');
        } else {
          setStepStatus((prev) => ({ ...prev, 3: STEP_STATUS.COMPLETED }));
          setStepSummaries((prev) => ({ ...prev, 3: 'Could not verify' }));
          setCannotVerifyAttempt((attempt) => attempt + 1);
          setCannotVerifyTriggered(true);
        }
      },
      onError: (error, variables) => {
        toast.error(error?.message ?? 'Could not verify, please try again');
        Step3CtaResponseEvent({
          ...jumboIdentity,
          [MetricsForJumboEvent.feature_meta]: {
            success: false,
            can_verify: variables?.body?.can_verify,
            error: error?.message,
          },
        });
      },
    },
    null,
    ['copilot-submit-verification', orderId],
    { doNotShowSuccessToast: true },
    ({ body, sessionId: sid }) => copilotModel.submitVerification(body, sid),
  );

  // Polls `evaluate_resolution`, firing as soon as step 4 opens - there's no
  // separate "confirm" call first for this stage, `submitVerification`
  // already did that job.
  const { data: evaluateResolutionData, status: evaluateResolutionStatus } =
    useCopilotPolling({
      enabled: openStep === 4 && orderId,
      queryKey: [
        'copilot-evaluate-resolution',
        orderId,
        sessionId,
        evaluateAttempt,
      ],
      queryFn: () => copilotModel.evaluateResolution(orderId, sessionId),
    });

  // Polls `cannot_verify_response`, starting once `submit_verification` has
  // actually fired with `can_verify: false` (see above) - same "fired
  // server-side, no separate confirm call" shape as `evaluate_resolution`.
  const { data: cannotVerifyResponseData, status: cannotVerifyResponseStatus } =
    useCopilotPolling({
      enabled: cannotVerifyTriggered && orderId,
      queryKey: [
        'copilot-cannot-verify-response',
        orderId,
        sessionId,
        cannotVerifyAttempt,
      ],
      queryFn: () => copilotModel.cannotVerifyResponse(orderId, sessionId),
    });

  // `data.options` always holds exactly one entry here (`slug:
  // "customer_response"`) - the drafted message itself, not a list of
  // choices like every other step's `options` (see copilotModel).
  const cannotVerifyResponseMessage =
    cannotVerifyResponseData?.options?.[0]?.display_text ?? '';

  // Bumping the attempt changes `cannot_verify_response`'s query key, so
  // `useCopilotPolling` starts a genuinely fresh poll (PENDING again)
  // rather than continuing to serve the failed attempt's cached result.
  const handleRetryCannotVerifyResponse = () => {
    setCannotVerifyAttempt((attempt) => attempt + 1);
  };

  // Fires once per "Cannot Verify" attempt, when the drafted response resolves.
  const firedCannotVerifyResponseForAttemptRef = useRef(null);
  useEffect(() => {
    if (
      cannotVerifyResponseStatus !== COPILOT_POLL_STATUS.COMPLETED ||
      firedCannotVerifyResponseForAttemptRef.current === cannotVerifyAttempt
    ) {
      return;
    }
    firedCannotVerifyResponseForAttemptRef.current = cannotVerifyAttempt;
    Step3CannotVerifyResponseShownEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        recommended_response: cannotVerifyResponseMessage,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cannotVerifyResponseStatus, cannotVerifyAttempt]);

  const isServiceDenied = Boolean(evaluateResolutionData?.is_service_denied);

  const recommendedResolutionItems = toResolutionItems(
    evaluateResolutionData?.recommended_resolution?.items,
  );
  const alternateResolutionItems = toResolutionItems(
    evaluateResolutionData?.alternate_resolution?.items,
  );
  const deniedItems = toDeniedItems(evaluateResolutionData?.denied_items);
  const recommendedResolutionMeta = {
    options: toSourceOptions(
      evaluateResolutionData?.recommended_resolution?.options,
    ),
  };
  const alternateResolutionMeta = {
    available: evaluateResolutionData?.alternate_resolution?.available ?? false,
    description:
      evaluateResolutionData?.alternate_resolution?.description ?? '',
    options: toSourceOptions(
      evaluateResolutionData?.alternate_resolution?.options,
    ),
  };

  // Fires once per attempt, once the evaluate_resolution poll resolves.
  const firedStep4OpenedForAttemptRef = useRef(null);
  useEffect(() => {
    if (
      openStep !== 4 ||
      evaluateResolutionStatus !== COPILOT_POLL_STATUS.COMPLETED ||
      firedStep4OpenedForAttemptRef.current === evaluateAttempt
    ) {
      return;
    }
    firedStep4OpenedForAttemptRef.current = evaluateAttempt;
    Step4OpenedEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        recommended_items: JSON.stringify(
          recommendedResolutionItems.map((item) => ({
            item_id: item.id,
            resolution_type: item.resolutionType,
          })),
        ),
        alternate_available: alternateResolutionMeta.available,
        is_service_denied: isServiceDenied,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openStep, evaluateResolutionStatus, evaluateAttempt]);

  // `/resolve`'s response drives step 4's post-resolve confirmation screen -
  // it's a terminal action, so unlike the rest of the flow there's no
  // "advance regardless" fallback: if it fails, the agent stays on the
  // Resolution form and can retry.
  const { mutate: resolve } = useMutationCall(
    {
      onSuccess: (res) => {
        if (res?.success === false) {
          toast.error(res?.message ?? 'Could not initiate resolution');
          Step4CtaResponseEvent({
            ...jumboIdentity,
            [MetricsForJumboEvent.feature_meta]: {
              success: false,
              error: res?.message,
            },
          });
          return;
        }
        Step4CtaResponseEvent({
          ...jumboIdentity,
          [MetricsForJumboEvent.feature_meta]: {
            success: true,
            title: res?.title,
          },
        });
        setResolutionResult({
          title: res?.title ?? 'Resolution Initiated',
          tagOptions: toTagOptions(res?.manual_tags),
        });
        completeStep(4, 'Resolved');
      },
      onError: (error) => {
        toast.error(
          error?.message ?? 'Could not initiate resolution, please try again',
        );
        Step4CtaResponseEvent({
          ...jumboIdentity,
          [MetricsForJumboEvent.feature_meta]: {
            success: false,
            error: error?.message,
          },
        });
      },
    },
    null,
    ['copilot-resolve', orderId],
    { doNotShowSuccessToast: true },
    ({ body, sessionId: sid }) => copilotModel.resolve(body, sid),
  );

  // "Close Chat" on the post-resolve screen - submits whatever tags were
  // picked in "Add Tags" via the legacy generic `addManualTag` endpoint
  // (same one the non-Copilot tag flow uses) before actually closing the
  // panel. Fires regardless of whether any tag calls fail, since closing
  // the chat shouldn't be blocked by a tagging error - the agent just gets
  // a toast and the panel still closes.
  const handleResolutionClose = async (selectedTagSlugs) => {
    const { tagOptions = [] } = resolutionResult || {};
    const tagIds = selectedTagSlugs
      .map((slug) => tagOptions.find((tag) => tag.value === slug)?.id)
      .filter((id) => id != null);

    Step4TagsAddedOnCloseEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        tags: JSON.stringify(selectedTagSlugs),
      },
    });

    if (tagIds.length > 0) {
      const results = await Promise.allSettled(
        tagIds.map((tagId) =>
          tagsModel.addManualTag(COPILOT_ADD_MANUAL_TAG_URL, {
            order_id: orderId,
            tag_id: tagId,
          }),
        ),
      );
      if (results.some((result) => result.status === 'rejected')) {
        toast.error('Could not save some tags');
      }
    }
    onClose();
  };

  // Wraps setProductCounts to report each add/remove/quantity change individually.
  const handleProductCountsChange = (nextCounts) => {
    Object.keys(nextCounts).forEach((itemId) => {
      const previousCount = productCounts[itemId] || 0;
      const newCount = nextCounts[itemId] || 0;
      if (previousCount === newCount) return;
      Step1ProductSelectionChangedEvent({
        ...jumboIdentity,
        [MetricsForJumboEvent.feature_meta]: {
          item_id: itemId,
          action:
            newCount === 0
              ? 'remove'
              : previousCount === 0
              ? 'add'
              : 'quantity_changed',
          previous_count: previousCount,
          new_count: newCount,
        },
      });
    });
    setProductCounts(nextCounts);
  };

  // Tracks every complaint-type change in step 2, skipping the first render.
  const isFirstComplaintTypeRender = useRef(true);
  useEffect(() => {
    if (isFirstComplaintTypeRender.current) {
      isFirstComplaintTypeRender.current = false;
      return;
    }
    if (!selectedComplaintParentSlug) return;
    Step2ComplaintTypeChangedEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        parent_slug: selectedComplaintParentSlug,
        sub_slug: selectedComplaintSubSlug,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComplaintParentSlug, selectedComplaintSubSlug]);

  // Step 2 only opens once "confirm_items" actually succeeds.
  const { mutate: confirmItems } = useMutationCall(
    {
      onSuccess: (res, selectedProducts) => {
        if (res?.success === false) {
          toast.error(res?.message ?? 'Could not confirm items');
          Step1CtaResponseEvent({
            ...jumboIdentity,
            [MetricsForJumboEvent.feature_meta]: {
              success: false,
              error: res?.message,
            },
          });
          return;
        }
        Step1CtaResponseEvent({
          ...jumboIdentity,
          [MetricsForJumboEvent.feature_meta]: { success: true },
        });
        setConfirmItemsResult({
          selectedItems: res?.selected_items,
          options: res?.options,
        });
        // A redo (reopening step 1 with a different selection) resolves a
        // new complaint-type option set, which may no longer contain the
        // previously-picked parent/sub slug - clear them so a stale slug
        // can't be confirmed against complaint-type options it no longer
        // matches.
        setSelectedComplaintParentSlug(null);
        setSelectedComplaintSubSlug(null);
        const totalUnits = selectedProducts.reduce(
          (sum, product) => sum + (product.count || 0),
          0,
        );
        completeStep(
          1,
          `${totalUnits} unit${totalUnits === 1 ? '' : 's'} selected`,
        );
      },
      onError: (error) => {
        toast.error(
          error?.message ?? 'Could not confirm items, please try again',
        );
        Step1CtaResponseEvent({
          ...jumboIdentity,
          [MetricsForJumboEvent.feature_meta]: {
            success: false,
            error: error?.message,
          },
        });
      },
    },
    null,
    ['copilot-confirm-items', orderId, cartId],
    { doNotShowSuccessToast: true },
    (selectedProducts) =>
      copilotModel.confirmItems(
        {
          order_id: orderId,
          cart_id: cartId,
          selected_items: selectedProducts.map((product) => ({
            id: product.id,
            product_id: product.productId,
            quantity: product.count,
          })),
        },
        sessionId,
      ),
  );

  // Pending steps aren't reachable yet - only a step that's already active
  // or completed can be toggled open/closed by hand.
  const handleToggleStep = (stepId) => {
    if (stepStatus[stepId] === STEP_STATUS.PENDING) return;
    setOpenStep((prev) => (prev === stepId ? null : stepId));
  };

  const completeStep = (stepId, summary) => {
    setStepStatus((prev) => ({ ...prev, [stepId]: STEP_STATUS.COMPLETED }));
    setStepSummaries((prev) => ({ ...prev, [stepId]: summary }));
    const nextStepId = stepId + 1;
    if (COPILOT_STEPS.some((step) => step.id === nextStepId)) {
      setStepStatus((prev) => ({
        ...prev,
        [nextStepId]: STEP_STATUS.ACTIVE,
      }));
      setOpenStep(nextStepId);
    } else {
      setOpenStep(null);
    }
  };

  // Step 1/2/3 each only advance from their mutation's own "onSuccess" now -
  // these handlers just fire the request.
  const handleOrderConfirm = (selectedProducts) => {
    Step1CtaClickedEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        selected_items: JSON.stringify(
          selectedProducts.map((product) => ({
            item_id: product.id,
            product_id: product.productId,
            quantity: product.count,
          })),
        ),
      },
    });
    confirmItems(selectedProducts);
  };

  const handleComplaintConfirm = (slug) => {
    Step2CtaClickedEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: { slug },
    });
    confirmComplaint(slug);
  };

  // Both "Verify" and "Cannot Verify" call `/submit_verification` -
  // `can_verify` is what tells the backend which one the agent hit.
  // "grievance" is the per-row slug picked from "confirm_complaint"'s own
  // `grievance_level` options (see `grievanceByRowKey`); the field is a
  // plain Go `string`, not a pointer, so a row with no selection still
  // sends `''` rather than `null`/omitting the key. A split item sends two
  // separate rows here, same `id`/`product_id`, each with its own
  // `quantity`/`grievance`.
  const handleVerificationComplete = ({ checkedStepIds, verified }) => {
    const uncheckedStepIds = verificationSteps
      .map((step) => step.id)
      .filter((id) => !checkedStepIds?.has(id));
    Step3CtaClickedEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        verified,
        unchecked_step_ids: JSON.stringify(uncheckedStepIds),
        grievance_selections: JSON.stringify(grievanceByRowKey),
      },
    });
    submitVerification({
      body: {
        order_id: orderId,
        verification_slug: Array.from(checkedStepIds || []),
        verification_slug_pending: uncheckedStepIds,
        can_verify: verified,
        items: step3Rows.map((row) => ({
          id: row.id,
          product_id: row.productId,
          quantity: row.count,
          grievance: row.selectedGrievance || '',
        })),
      },
      sessionId,
    });
  };

  // No resolution-service remedy exists for this complaint - "Proceed"
  // doesn't call `/resolve` at all, it just resolves the UI client-side
  // using the tags already carried on the evaluate response.
  const handleServiceDenialComplete = () => {
    Step4CtaClickedEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: { is_service_denied: true },
    });
    Step4CtaResponseEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        success: true,
        is_service_denied: true,
      },
    });
    setResolutionResult({
      title: 'No Service Offered',
      isServiceDenied: true,
      tagOptions: toTagOptions(evaluateResolutionData?.manual_tags),
    });
    completeStep(4, 'Service Denied');
  };

  const handleResolutionComplete = ({
    isServiceDenied: isServiceDenialComplete,
    chosenResolution,
    refundSource,
    items,
  }) => {
    if (isServiceDenialComplete) {
      handleServiceDenialComplete();
      return;
    }
    Step4CtaClickedEvent({
      ...jumboIdentity,
      [MetricsForJumboEvent.feature_meta]: {
        chosen_resolution: chosenResolution,
        refund_source: refundSource,
        items: JSON.stringify(
          items.map((item) => ({
            item_id: item.id,
            product_id: item.productId,
            quantity: item.quantity,
            resolution_type: item.resolutionType,
            pickup: item.pickup,
          })),
        ),
      },
    });
    resolve({
      body: {
        order_id: orderId,
        chosen_resolution: chosenResolution,
        refund_source: refundSource,
        items: items.map((item) => ({
          id: item.id,
          product_id: item.productId,
          quantity: item.quantity,
          resolution_type: item.resolutionType,
          pickup: item.pickup,
        })),
      },
      sessionId,
    });
  };

  const renderStepContent = (stepId) => {
    switch (stepId) {
      case 1:
        if (isError) {
          return (
            <p className='tw-text-[12px] tw-font-regular tw-text-red-500 tw-m-0'>
              Could not load order items.
            </p>
          );
        }
        return (
          <OrderProductConfirmation
            isLoading={isLoading}
            items={items}
            counts={productCounts}
            onCountsChange={handleProductCountsChange}
            onConfirm={handleOrderConfirm}
          />
        );
      case 2:
        return (
          <ComplaintIdentification
            products={step2Products}
            images={complaintImages}
            complaintTypeOptions={complaintTypeOptions}
            onConfirm={handleComplaintConfirm}
            guidanceStatus={complaintIdentificationStatus}
            selectedParentSlug={selectedComplaintParentSlug}
            onSelectedParentSlugChange={setSelectedComplaintParentSlug}
            selectedSubSlug={selectedComplaintSubSlug}
            onSelectedSubSlugChange={setSelectedComplaintSubSlug}
            orderId={orderId}
            cartId={cartId}
            customerId={customerId}
          />
        );
      case 3:
        return (
          <Verification
            products={step3Rows}
            images={complaintImages}
            verificationSteps={verificationSteps}
            onComplete={handleVerificationComplete}
            guidanceStatus={confirmComplaintResult?.status ?? null}
            checkedIds={checkedVerificationIds}
            onCheckedIdsChange={setCheckedVerificationIds}
            onSplitRow={handleSplitRow}
            onMergeRow={handleMergeRow}
            onGrievanceChange={(rowKey, itemId, grievanceSlug) => {
              setGrievanceByRowKey((prev) => ({
                ...prev,
                [rowKey]: grievanceSlug,
              }));
              Step3GrievanceLevelSelectedEvent({
                ...jumboIdentity,
                [MetricsForJumboEvent.feature_meta]: {
                  item_id: itemId,
                  row_key: rowKey,
                  grievance_level_slug: grievanceSlug,
                },
              });
            }}
            orderId={orderId}
            cartId={cartId}
            customerId={customerId}
          />
        );
      case 4:
        return (
          <Resolution
            recommendedItems={recommendedResolutionItems}
            alternateItems={alternateResolutionItems}
            recommendedMeta={recommendedResolutionMeta}
            alternateMeta={alternateResolutionMeta}
            deniedItems={deniedItems}
            isServiceDenied={isServiceDenied}
            onComplete={handleResolutionComplete}
            guidanceStatus={evaluateResolutionStatus}
            orderId={orderId}
            cartId={cartId}
            customerId={customerId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className='dock-font tw-flex tw-flex-col tw-h-full tw-w-full tw-bg-white tw-rounded-bl-[16px] tw-rounded-tl-[16px] tw-overflow-hidden'>
      {/* Scrollable region - header + step list. The CannotVerifyResponse
          card below is deliberately OUTSIDE this region (own flex item, not
          `overflow-y-auto`) so it stays pinned to the bottom of the panel
          while the steps above it scroll independently. */}
      <div className='tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-[16px] tw-pt-[20px] tw-gap-[26px]'>
        <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-min-w-0'>
          <p className='tw-text-[18px] tw-font-medium tw-text-grey-900 tw-m-0 tw-leading-none'>
            Copilot
          </p>
          <button
            type='button'
            onClick={handleClose}
            className='tw-border-none tw-bg-transparent tw-p-1 tw-cursor-pointer tw-flex tw-items-center tw-shrink-0'
          >
            <X size={20} className='tw-text-[#6c737f]' />
          </button>
        </div>

        {resolutionResult ? (
          // `/resolve` succeeding replaces the whole step flow with its own
          // standalone screen (Figma node 2219-41572), not step 4's content.
          <ResolutionConfirmation
            title={resolutionResult.title}
            tagOptions={resolutionResult.tagOptions}
            isServiceDenied={resolutionResult.isServiceDenied}
            onCloseChat={handleResolutionClose}
          />
        ) : (
          <div className='tw-flex tw-flex-col tw-gap-[12px] tw-w-full tw-pb-[20px]'>
            {COPILOT_STEPS.map((step) => (
              <AccordionStep
                key={step.id}
                stepNumber={step.id}
                title={step.title}
                status={stepStatus[step.id]}
                subtitle={stepSummaries[step.id]}
                isOpen={openStep === step.id}
                onToggle={() => handleToggleStep(step.id)}
              >
                {renderStepContent(step.id)}
              </AccordionStep>
            ))}
          </div>
        )}
      </div>

      {!resolutionResult && cannotVerifyTriggered && openStep === 3 && (
        // Pinned below the scrollable region (Figma node 2219-41284), not a
        // full-screen takeover - step 4 "Resolution" stays locked/pending
        // in the step list above (never advanced to ACTIVE, so it's not
        // reachable), since there's nothing to resolve once verification
        // has failed. Gated on `openStep === 3` too, not just
        // `cannotVerifyTriggered`, so navigating away from step 3 (e.g. a
        // later successful "Verify" opening step 4) hides this immediately
        // even before the triggering flag itself gets reset.
        <div className='tw-flex-shrink-0 tw-w-full tw-bg-white tw-border-0 tw-border-t tw-border-solid tw-border-[#e5e7eb] tw-px-[16px] tw-py-[16px]'>
          <CannotVerifyResponse
            message={cannotVerifyResponseMessage}
            guidanceStatus={cannotVerifyResponseStatus}
            onRetry={handleRetryCannotVerifyResponse}
          />
        </div>
      )}
    </div>
  );
};

export default CopilotView;
